"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useRouter } from "next/navigation";
import {
  login as apiLogin,
  logout as apiLogout,
  getCurrentUser,
  isAuthenticated as checkAuth,
  verify2FA as apiVerify2FA,
  resend2FACode as apiResend2FACode,
  acceptLegalDocuments as apiAcceptLegal,
} from "@/lib/api";
import type { User, APIError } from "@/lib/api";

// Session storage keys for 2FA state persistence
const TWOFA_REQUIRED_KEY = "vpp_2fa_required";
const TWOFA_TOKEN_KEY = "vpp_2fa_partial_token";
// Session storage key for combined legal-acceptance state (issue #143)
const LEGAL_REQUIRED_KEY = "vpp_legal_required";
// Legacy key kept ONLY for cleanup on mount during rollout. Will be removed
// after one release cycle.
const LEGACY_TOF_REQUIRED_KEY = "vpp_tof_required";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  requires2FA: boolean;
  pendingPartialToken: string | null;
  requiresLegalAcceptance: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  complete2FA: (code: string) => Promise<boolean>;
  resend2FA: () => Promise<boolean>;
  cancel2FA: () => void;
  acceptLegalDocuments: () => Promise<boolean>;
  declineLegalDocuments: () => void;
  logout: () => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * True if the user has NOT accepted both legal docs yet (Terms of Use AND
 * Data Protection Notice, issue #143). Either missing flag triggers the
 * first-login acceptance dialog.
 */
function legalAcceptancePending(u: User | null | undefined): boolean {
  if (!u) return false;
  return !u.has_accepted_tof || !u.has_accepted_dpn;
}

// Helper to get initial 2FA state from sessionStorage
function getInitial2FAState(): { requires2FA: boolean; partialToken: string | null } {
  if (typeof window === "undefined") {
    return { requires2FA: false, partialToken: null };
  }
  const requires2FA = sessionStorage.getItem(TWOFA_REQUIRED_KEY) === "true";
  const partialToken = sessionStorage.getItem(TWOFA_TOKEN_KEY);
  return { requires2FA, partialToken };
}

// Helper to save 2FA state to sessionStorage
function save2FAState(requires2FA: boolean, partialToken: string | null) {
  if (typeof window === "undefined") return;
  if (requires2FA && partialToken) {
    sessionStorage.setItem(TWOFA_REQUIRED_KEY, "true");
    sessionStorage.setItem(TWOFA_TOKEN_KEY, partialToken);
  } else {
    sessionStorage.removeItem(TWOFA_REQUIRED_KEY);
    sessionStorage.removeItem(TWOFA_TOKEN_KEY);
  }
}

// Helper to get initial legal-acceptance state from sessionStorage
function getInitialLegalState(): boolean {
  if (typeof window === "undefined") return false;
  return sessionStorage.getItem(LEGAL_REQUIRED_KEY) === "true";
}

// Helper to save legal-acceptance state to sessionStorage
function saveLegalState(requiresLegal: boolean) {
  if (typeof window === "undefined") return;
  if (requiresLegal) {
    sessionStorage.setItem(LEGAL_REQUIRED_KEY, "true");
  } else {
    sessionStorage.removeItem(LEGAL_REQUIRED_KEY);
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize 2FA state from sessionStorage
  const [requires2FA, setRequires2FAState] = useState(false);
  const [pendingPartialToken, setPendingPartialTokenState] = useState<string | null>(null);
  // Initialize legal-acceptance state from sessionStorage
  const [requiresLegalAcceptance, setRequiresLegalAcceptanceState] = useState(false);
  const router = useRouter();

  // Centralized setter for the partial token: updates React state AND
  // sessionStorage in one place so callers (login, resend, complete, cancel)
  // can't forget to sync one of them.
  const setPendingPartialToken = useCallback((token: string | null) => {
    setPendingPartialTokenState(token);
    if (token) {
      setRequires2FAState(true);
      save2FAState(true, token);
    } else {
      setRequires2FAState(false);
      save2FAState(false, null);
    }
  }, []);

  // Check for existing auth and restore 2FA/legal state on mount
  useEffect(() => {
    const checkExistingAuth = async () => {
      // One-time cleanup of the legacy ToF-only sessionStorage key (issue #143
      // generalised the gate from ToF-only to ToF+DPN; old sessions may still
      // hold the legacy key).
      if (typeof window !== "undefined") {
        sessionStorage.removeItem(LEGACY_TOF_REQUIRED_KEY);
      }

      // Restore 2FA state from sessionStorage
      const { requires2FA: restored2FA, partialToken } = getInitial2FAState();
      if (restored2FA && partialToken) {
        setRequires2FAState(true);
        setPendingPartialTokenState(partialToken);
      }

      // Restore legal-acceptance state from sessionStorage
      const restoredLegal = getInitialLegalState();
      if (restoredLegal) {
        setRequiresLegalAcceptanceState(true);
      }

      if (checkAuth()) {
        try {
          const currentUser = await getCurrentUser();
          setUser(currentUser);
          // Check if legal acceptance is needed (user logged in but missing
          // ToF or DPN acceptance — issue #143)
          if (legalAcceptancePending(currentUser)) {
            setRequiresLegalAcceptanceState(true);
            saveLegalState(true);
          }
        } catch {
          // Token is invalid, clear it
          apiLogout();
          setUser(null);
        }
      }
      setIsLoading(false);
    };

    checkExistingAuth();
  }, []);

  const login = useCallback(
    async (username: string, password: string): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await apiLogin(username, password);

        if (result.requires2FA) {
          // 2FA required - centralized setter handles React state + sessionStorage
          setPendingPartialToken(result.partialToken!);
          router.push("/auth/2fa");
          return true; // Partial success - need 2FA
        }

        // No 2FA - login complete
        setUser(result.user!);

        // Check if legal acceptance is needed (issue #143 — both docs)
        if (legalAcceptancePending(result.user)) {
          setRequiresLegalAcceptanceState(true);
          saveLegalState(true);
          router.push("/auth/tof");
          return true; // Partial success - need legal acceptance
        }

        router.push("/dashboard");
        return true;
      } catch (err) {
        const apiError = err as APIError;
        setError(apiError.detail || "Login failed. Please try again.");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [router, setPendingPartialToken]
  );

  const complete2FA = useCallback(
    async (code: string): Promise<boolean> => {
      if (!pendingPartialToken) {
        setError("No pending 2FA session");
        return false;
      }

      setIsLoading(true);
      setError(null);

      try {
        const user = await apiVerify2FA(pendingPartialToken, code);
        setUser(user);
        // Centralized clear (React state + sessionStorage)
        setPendingPartialToken(null);

        // Check if legal acceptance is needed (issue #143 — both docs)
        if (legalAcceptancePending(user)) {
          setRequiresLegalAcceptanceState(true);
          saveLegalState(true);
          router.push("/auth/tof");
          return true; // Partial success - need legal acceptance
        }

        router.push("/dashboard");
        return true;
      } catch (err) {
        const apiError = err as APIError;
        setError(apiError.detail || "Invalid verification code");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [pendingPartialToken, router, setPendingPartialToken]
  );

  const resend2FA = useCallback(async (): Promise<boolean> => {
    if (!pendingPartialToken) {
      setError("No pending 2FA session");
      return false;
    }
    try {
      const response = await apiResend2FACode(pendingPartialToken);
      // Swap in the fresh partial_token so the verification window stays
      // aligned with the newly-issued OTP.
      setPendingPartialToken(response.partial_token);
      setError(null);
      return true;
    } catch (err) {
      const apiError = err as APIError;
      setError(apiError.detail || "Failed to resend code");
      return false;
    }
  }, [pendingPartialToken, setPendingPartialToken]);

  const cancel2FA = useCallback(() => {
    setPendingPartialToken(null);
    setError(null);
    router.push("/auth");
  }, [router, setPendingPartialToken]);

  const acceptLegalDocuments = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const updatedUser = await apiAcceptLegal();
      setUser(updatedUser);
      // Clear legal-acceptance gate
      setRequiresLegalAcceptanceState(false);
      saveLegalState(false);
      router.push("/dashboard");
      return true;
    } catch (err) {
      const apiError = err as APIError;
      setError(apiError.detail || "Failed to record acceptance");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [router]);

  const declineLegalDocuments = useCallback(() => {
    // User declined - log them out
    apiLogout();
    setUser(null);
    setError(null);
    setPendingPartialToken(null);
    setRequiresLegalAcceptanceState(false);
    saveLegalState(false);
    router.push("/auth");
  }, [router, setPendingPartialToken]);

  const logout = useCallback(() => {
    apiLogout();
    setUser(null);
    setError(null);
    setPendingPartialToken(null);
    setRequiresLegalAcceptanceState(false);
    saveLegalState(false);
    router.push("/auth");
  }, [router, setPendingPartialToken]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    error,
    requires2FA,
    pendingPartialToken,
    requiresLegalAcceptance,
    login,
    complete2FA,
    resend2FA,
    cancel2FA,
    acceptLegalDocuments,
    declineLegalDocuments,
    logout,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
